import React from 'react';
import styled from 'styled-components';

import { colors } from '../../../tokens/colors/colors';
import { font } from '../../../style/text';
import { deviceWidthMeasurements } from '../../../style/size';
import { MerchantProductOfferCard, MerchantProductOfferPlaceholderCards } from './MerchantProductOfferCard';
import { isMobileDevice, useViewportSize } from '../../../style/utils';
import { NO_SCROLLBAR_CSS_MIXIN } from '../../../style/styleConstants';
import { getLocalizedTexts } from '../../../Locales';
import { useMerchantIdToMerchantNameMap } from '../../../lib/productDiscovery/displayLogic';
import { MerchantProductOffer } from '../../../lib/productDiscovery/conversationMessages';

export function FavoriteMerchantProductOffersTab({
    savedMerchantProductOffers,
    setSavedMerchantProductOffers,
    unsavedMerchantProductOffersStillDisplayed,
    setUnsavedMerchantProductOffersStillDisplayed,
}: {
    savedMerchantProductOffers: MerchantProductOffer[];
    setSavedMerchantProductOffers: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
    unsavedMerchantProductOffersStillDisplayed: MerchantProductOffer[];
    setUnsavedMerchantProductOffersStillDisplayed: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
}) {
    const { width: viewportWidth } = useViewportSize();
    const merchantIdToMerchantNameMap = useMerchantIdToMerchantNameMap({
        merchantProductOffers: savedMerchantProductOffers,
    });
    if (savedMerchantProductOffers === undefined) return <MerchantProductOfferPlaceholderCards {...{ viewportWidth }} />;
    if (savedMerchantProductOffers.length === 0) {
        const texts = getLocalizedTexts().productDiscovery.favoritesTab;
        return (
            <EmptyStateContainer>
                <EmptyStateTitle>{texts.emptyState.title}</EmptyStateTitle>
                <EmptyStateSubtitle $isMobileDevice={isMobileDevice}>{texts.emptyState.subtitle}</EmptyStateSubtitle>
            </EmptyStateContainer>
        );
    }
    return (
        <FavoriteProductsContainer $isMobileDevice={isMobileDevice}>
            <GridContainer $isMobileDevice={isMobileDevice}>
                {savedMerchantProductOffers.map((offer) => (
                    <MerchantProductOfferCard
                        {...{
                            key: offer.merchantProductOfferId,
                            offer,
                            merchantIdToMerchantNameMap,
                            savedMerchantProductOffers,
                            setSavedMerchantProductOffers,
                            isFavoritesTab: true,
                            setUnsavedMerchantProductOffersStillDisplayed,
                            unsavedMerchantProductOffersStillDisplayed,
                        }}
                    />
                ))}
            </GridContainer>
        </FavoriteProductsContainer>
    );
}

const FavoriteProductsContainer = styled.div<{ $isMobileDevice: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding-top: 24px;
    background-color: ${($isMobileDevice) => ($isMobileDevice ? colors.background.default : colors.background.light)};
    overflow-y: auto;

    ${({ $isMobileDevice }) => ($isMobileDevice ? `align-items: center;` : '')}

    ${NO_SCROLLBAR_CSS_MIXIN}
`;

const EmptyStateContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: calc(100vh - 48px); /* Subtract header height */
    width: 100%;
    gap: 16px;
    padding: 0 16px;
    box-sizing: border-box;
`;

const EmptyStateTitle = styled.div`
    font-family: ${font.ambitBold};
    font-size: 20px;
    color: ${colors.content.default};
    text-align: center;
`;

const EmptyStateSubtitle = styled.div<{ $isMobileDevice: boolean }>`
    font-family: ${font.ambitRegular};
    font-size: 16px;
    color: ${colors.content.secondary};
    text-align: center;
    max-width: ${({ $isMobileDevice }) => ($isMobileDevice ? '280px' : 'none')};
    white-space: ${({ $isMobileDevice }) => ($isMobileDevice ? 'normal' : 'nowrap')};
`;

const GridContainer = styled.div<{ $isMobileDevice: boolean }>`
    display: grid;
    width: calc(100% - 32px);
    margin: 0 16px;
    padding-bottom: 24px;
    ${({ $isMobileDevice }) =>
        !$isMobileDevice
            ? `
                grid-template-columns: repeat(7, 1fr);
                gap: 12px 24px;
                justify-items: center;

                @media (max-width: ${deviceWidthMeasurements.xLarge}) {
                    grid-template-columns: repeat(6, 1fr);
                }

                @media (max-width: ${deviceWidthMeasurements.large}) {
                    grid-template-columns: repeat(5, 1fr);
                }

                @media (max-width: ${deviceWidthMeasurements.medium}) {
                    grid-template-columns: repeat(4, 1fr);
                }
            `
            : `
                grid-template-columns: repeat(2, 1fr);
                gap: 16px 8px;
                align-items: center;
                justify-items: center;
            `};
`;
